import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { api } from "@/api";
import { Search } from "lucide-react";

const rs = (v) => (v === null || v === undefined || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);

export default function RateFinder() {
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/rates").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t
      ? items.filter((i) =>
          [i.name, i.company, i.quality].some((v) => String(v || "").toLowerCase().includes(t))
        )
      : items;
    return [...list].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [items, q]);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1100px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Rate Finder</h1>
          <p className="mt-2 text-sm text-zinc-600">Search any item to see its sale price.</p>
        </div>

        <div className="relative py-6">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            autoFocus
            data-testid="rate-finder-search"
            className="rounded-sm pl-9"
            placeholder="Search item, company or quality"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="overflow-x-auto border border-zinc-200 bg-white">
          <table className="w-full text-sm" data-testid="rate-finder-table">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em]">
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Company</th>
                <th className="px-3 py-2 text-left">Quality</th>
                <th className="px-3 py-2 text-right">Sale price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-10 text-center text-sm text-zinc-500">
                    No item matches that search.
                  </td>
                </tr>
              ) : (
                rows.map((i) => (
                  <tr key={i.id} data-testid={`rate-row-${i.id}`}>
                    <td className="px-3 py-2 font-semibold uppercase">{i.name}</td>
                    <td className="px-3 py-2 uppercase text-zinc-600">{i.company || "—"}</td>
                    <td className="px-3 py-2 uppercase text-zinc-600">{i.quality || "—"}</td>
                    <td className="font-mono-num px-3 py-2 text-right font-semibold" data-testid={`rate-price-${i.id}`}>
                      {rs(i.sales_price)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-zinc-500" data-testid="rate-finder-count">
          {rows.length} item(s)
        </p>
      </main>
    </AppShell>
  );
}
